import { Link } from "wouter";
import {
  ArrowRight, Phone, Star, CheckCircle, TrendingUp, MapPin,
  Users, Award, Shield, Zap, BarChart3, Globe,
  Layers, Brain, Cpu, MessageSquare, Network,
  Search, FileText, DollarSign, Activity, Building2,
  Wrench, ChevronRight, Target, Clock, ThumbsUp,
} from "lucide-react";
import Layout from "@/components/Layout";
import { LeadForm, FAQSection, TestimonialsSection, CTASection } from "@/components/PageComponents";
import {
  SITE_NAME, SITE_PHONE,
  services, industries, stats, testimonials, faqs
} from "@/lib/siteData";

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const schema = [
  {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://locranking-7q2qajau.manus.space/#organization",
        name: "Local Ranking Coach, Inc.",
        alternateName: "Local Ranking Coach",
        url: "https://locranking-7q2qajau.manus.space",
        logo: "https://locranking-7q2qajau.manus.space/logo.png",
        telephone: "1-800-301-6220",
        email: "info@localrankingcoach.com",
        foundingDate: "2005",
        description:
          "Local Ranking Coach, Inc. is a veteran-owned local SEO company founded in 2005. We help small businesses, contractors, medical practices, and law firms rank in the Top 3 of Google Maps, Google AI Overviews, and local search results nationwide.",
        areaServed: {
          "@type": "Country",
          name: "United States",
        },
        address: {
          "@type": "PostalAddress",
          addressCountry: "US",
        },
        sameAs: [
          "https://www.facebook.com/localrankingcoach",
          "https://www.linkedin.com/company/localrankingcoach",
          "https://twitter.com/localrankcoach",
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Local SEO Services",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Google Business Profile Optimization" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Local SEO Services" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Google Maps Optimization" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "AI Overview SEO" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Technical SEO" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Content Marketing" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Link Building" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Reputation Management" } },
          ],
        },
      },
      {
        "@type": "WebSite",
        "@id": "https://locranking-7q2qajau.manus.space/#website",
        url: "https://locranking-7q2qajau.manus.space",
        name: "Local Ranking Coach",
        description: "Veteran-owned local SEO agency helping businesses rank in Google Maps, AI Overviews, and local search since 2005.",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://locranking-7q2qajau.manus.space/search?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "WebPage",
        "@id": "https://locranking-7q2qajau.manus.space/#webpage",
        url: "https://locranking-7q2qajau.manus.space/",
        name: "Local SEO for Businesses | Rank #1 in Google Maps & AI Overviews | Local Ranking Coach",
        description:
          "Local SEO services for small businesses, contractors, doctors, and attorneys. Veteran-owned. Founded 2005. Rank in Top 3 Google Maps, AI Overviews, and local search nationwide.",
        isPartOf: { "@id": "https://locranking-7q2qajau.manus.space/#website" },
        about: { "@id": "https://locranking-7q2qajau.manus.space/#organization" },
        breadcrumb: {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://locranking-7q2qajau.manus.space/" },
          ],
        },
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://locranking-7q2qajau.manus.space/#faq",
    mainEntity: [
      {
        "@type": "Question",
        name: "How long does local SEO take to show results?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Most businesses begin seeing measurable improvements in Google Maps rankings and local search visibility within 60 to 90 days of starting a properly executed local SEO campaign. Stronger results — including consistent Top 3 Google Maps placement, increased call volume, and qualified lead generation — typically develop over 4 to 6 months. Unlike paid advertising, which stops generating leads the moment you stop paying, local SEO builds compounding organic visibility that grows in value over time. The exact timeline depends on your market's competitiveness, your current Google Business Profile health, and the technical condition of your website.",
        },
      },
      {
        "@type": "Question",
        name: "Can you help rank my business in Google Maps?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Google Maps SEO — also known as Google Business Profile optimization and Local Pack optimization — is one of our primary specialties. We have been optimizing Google Business Profiles and Google Maps rankings since 2005, long before the platform was called Google Business Profile. Our process includes category optimization, service area configuration, photo strategy, review generation, citation building, and on-page local SEO signals that collectively push your business into the Top 3 Map Pack positions where the majority of clicks and calls are generated.",
        },
      },
      {
        "@type": "Question",
        name: "What is AI Overview SEO and why does it matter in 2026?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI Overview SEO is the practice of optimizing your website and online profiles to appear in AI-generated search summaries produced by Google AI Overviews, Google AI Mode, Bing Copilot, ChatGPT Search, Perplexity AI, and other large language model-powered search platforms. In 2026, AI Overviews appear for a significant percentage of local and informational searches, and businesses that are not optimized for these AI-generated results are losing visibility to competitors who are. Our AI Overview SEO and AEO (Answer Engine Optimization) services use structured data, semantic content architecture, E-E-A-T signals, and entity optimization to ensure your business is cited and recommended by AI search engines.",
        },
      },
      {
        "@type": "Question",
        name: "Is local SEO worth it for small businesses?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Local SEO consistently delivers one of the highest returns on investment of any marketing channel available to small businesses. Businesses that rank in the Top 3 Google Maps positions receive the majority of local search clicks and calls for their target keywords. A well-executed local SEO campaign reduces your cost per lead over time, builds long-term brand authority, and generates qualified inbound leads from customers who are actively searching for your services — not passive audiences who may or may not need what you offer. For trades businesses, medical practices, and law firms, local SEO is often the single most impactful marketing investment available.",
        },
      },
      {
        "@type": "Question",
        name: "Do you work with contractors, doctors, and attorneys?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Contractors, medical practices, and law firms are our three primary industry verticals. We have built specialized local SEO systems for roofing companies, HVAC contractors, plumbers, electricians, general contractors, dental practices, medical clinics, chiropractors, personal injury attorneys, family law firms, and criminal defense lawyers. Each industry has unique search behavior, competitive dynamics, and conversion requirements that demand a specialized approach — not a generic SEO template.",
        },
      },
      {
        "@type": "Question",
        name: "What does local SEO actually do for a website?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Local SEO improves your website's visibility in geographically relevant search results, including Google Maps, the Local Pack, and organic search results for city-specific and 'near me' queries. Technically, it involves optimizing your Google Business Profile, building consistent NAP citations across directories, improving on-page signals like title tags, meta descriptions, schema markup, and location-specific content, earning local backlinks, and generating authentic customer reviews. The measurable outcomes include higher Google Maps rankings, increased website traffic from local searches, more inbound phone calls, more form submissions, and ultimately more revenue from customers who found you through organic search.",
        },
      },
      {
        "@type": "Question",
        name: "What is the difference between local SEO and traditional SEO?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Traditional SEO focuses on ranking for broad, national, or informational keywords in organic search results. Local SEO focuses specifically on ranking for geographically qualified searches — 'plumber near me,' 'HVAC repair Tampa,' 'dentist in Orlando' — and on appearing in the Google Maps Local Pack, which shows the top 3 local businesses for a given search. Local SEO requires optimization of your Google Business Profile, local citation consistency, location-specific landing pages, and proximity-based ranking signals that traditional SEO does not address. For service-area businesses and brick-and-mortar locations, local SEO is the more relevant and higher-ROI discipline.",
        },
      },
      {
        "@type": "Question",
        name: "How much does local SEO cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Local SEO pricing varies based on your market's competitiveness, the number of service areas you target, and the scope of services required. Our Starter Plan begins at an entry-level investment suitable for single-location businesses in moderately competitive markets. Our Growth Plan is designed for businesses targeting multiple service areas or operating in highly competitive markets. Our Authority Plan provides full-scale local SEO with content marketing, link building, and AI Overview optimization for businesses seeking market dominance. We provide transparent pricing with no hidden fees and no long-term contracts. Contact us for a free SEO audit and customized proposal.",
        },
      },
    ],
  },
];

const hreflangLinks = [
  { hreflang: "en-us", href: "https://locranking-7q2qajau.manus.space/" },
  { hreflang: "en", href: "https://locranking-7q2qajau.manus.space/" },
  { hreflang: "x-default", href: "https://locranking-7q2qajau.manus.space/" },
];

// ─── Service Plans ────────────────────────────────────────────────────────────
const servicePlans = [
  {
    name: "Starter",
    tagline: "Launch Your Local Presence",
    price: "$1,250/mo",
    setupFee: "Setup Fee: $625",
    description: "Ideal for single-location businesses entering local SEO for the first time or cleaning up an underperforming Google Business Profile.",
    features: [
      "Google Business Profile Optimization",
      "10 Keywords",
      "Monthly Reporting",
      "Local Citation Setup",
      "6 Onsite Content Pages",
      "Google AI Search Optimization",
      "3 Long-Form Blog Creation",
      "10 Off-Page Link Building",
      "Local Citation Building (25/mo)",
      "On-Page SEO (up to 5 pages)",
      "Monthly Rank Tracking Report",
      "Review Response Templates",
      "Basic Schema Markup",
    ],
    cta: "Get Started",
    href: "/contact",
    highlight: false,
  },
  {
    name: "Growth",
    tagline: "Dominate Your Market",
    price: "$1,999/mo",
    setupFee: "Setup Fee: $1,000",
    description: "Built for businesses targeting multiple service areas, competitive markets, or those ready to move from page 2 to the Top 3 Google Maps positions.",
    features: [
      "Everything in Starter",
      "Local Citation Building (50/mo)",
      "On-Page SEO (up to 15 pages)",
      "Content Marketing (4 posts/mo)",
      "Google Maps Pack Optimization",
      "Competitor Gap Analysis",
      "Bi-Weekly Strategy Calls",
      "ChatGPT and Claude Optimization",
      "Schema Markup (for 10 Pages)",
      "Mention in Google AI Overview (3 Keywords)",
      "2 Press Releases a Month",
    ],
    cta: "Most Popular",
    href: "/contact",
    highlight: true,
  },
  {
    name: "Authority",
    tagline: "Own Your Industry",
    price: "$4,999/mo",
    setupFee: "Setup Fee: $1,800",
    description: "Full-scale local SEO and AI Overview optimization for businesses that want to dominate every relevant search result in their market.",
    features: [
      "Everything in Growth",
      "Local Citation Building (100+ directories)",
      "Content Marketing (6 posts/mo)",
      "Link Building Campaign",
      "Reputation Management",
      "Weekly Reporting & Calls",
      "5 Press Releases a Month",
      "Dedicated Account Manager",
      "ChatGPT, Claude, Gemini & Perplexity Optimization",
      "Mention in Google AI Overview (10 Keywords)",
    ],
    cta: "Get Authority",
    href: "/contact",
    highlight: false,
  },
];

// ─── Why Local SEO Matters ────────────────────────────────────────────────────
const seoImpactStats = [
  { value: "76%", label: "of local mobile searches result in a store visit within 24 hours" },
  { value: "28%", label: "of local searches result in a purchase within 24 hours" },
  { value: "92%", label: "of searchers pick businesses on the first page of local results" },
  { value: "3×", label: "more leads generated by businesses in the Top 3 Google Maps positions" },
];

// ─── What SEO Does for Your Website ──────────────────────────────────────────
const seoWebsiteImpacts = [
  {
    icon: TrendingUp,
    title: "Increases Organic Traffic Without Paying Per Click",
    body: "A properly optimized website ranks for dozens or hundreds of relevant local keywords simultaneously. Unlike Google Ads where you pay for every visitor, organic SEO traffic is free once you achieve rankings. Businesses that invest in local SEO reduce their cost per lead by an average of 61% compared to paid search over a 12-month period.",
  },
  {
    icon: MapPin,
    title: "Drives Google Maps Visibility & Phone Calls",
    body: "Your Google Business Profile is the most visible local SEO asset you own. Optimizing it for the correct primary category, service keywords, service areas, photos, and review signals directly determines whether you appear in the Top 3 Map Pack — the section that receives over 44% of all clicks for local searches.",
  },
  {
    icon: Shield,
    title: "Builds Long-Term Brand Authority",
    body: "Search engines measure authority through consistent signals: quality backlinks, positive reviews, accurate citations, and expert content. As these signals accumulate over time, your website becomes harder for competitors to displace. Businesses with strong local SEO authority maintain their rankings even through algorithm updates.",
  },
  {
    icon: Target,
    title: "Attracts High-Intent Buyers Ready to Convert",
    body: "Local SEO targets people who are actively searching for your service in your area right now — not passive social media scrollers. A homeowner searching 'emergency plumber Tampa' has immediate purchase intent. Ranking for these high-intent queries means every visitor is a potential customer, not just a traffic statistic.",
  },
  {
    icon: Activity,
    title: "Improves Website Conversion Rates",
    body: "SEO is not just about rankings. A comprehensive local SEO strategy includes conversion rate optimization: improving page speed, mobile usability, call-to-action placement, trust signals, and user experience. Businesses that combine ranking improvements with CRO typically see 2× to 4× more leads from the same amount of traffic.",
  },
  {
    icon: Globe,
    title: "Positions Your Business in AI-Generated Search Results",
    body: "Google AI Overviews, ChatGPT Search, Perplexity AI, and Bing Copilot now appear for millions of local searches daily. Businesses that are not optimized for AI-generated results are invisible to a growing segment of searchers. Our AI Overview SEO and AEO services ensure your business is cited and recommended across every major AI search platform.",
  },
];

// ─── Our Process ─────────────────────────────────────────────────────────────
const processSteps = [
  {
    step: "01",
    title: "SEO & Competitor Audit",
    body: "We begin every engagement with a comprehensive audit of your current rankings, Google Business Profile health, citation consistency, website technical performance, and competitor strategies. This baseline analysis identifies exactly what is holding your rankings back and what opportunities your competitors are not yet exploiting.",
  },
  {
    step: "02",
    title: "Google Business Profile Optimization",
    body: "Your Google Business Profile is the foundation of local search visibility. We optimize your primary and secondary categories, service descriptions, service area configuration, photo strategy, posting schedule, and Q&A section. Proper GBP optimization alone can move a business from outside the Local Pack to the Top 3 within 60 to 90 days in moderately competitive markets.",
  },
  {
    step: "03",
    title: "Semantic Content Development",
    body: "We build topically authoritative content clusters that establish your website as the definitive local resource for your industry. This includes service pages, city-specific landing pages, FAQ content, and blog articles — all structured around semantic keyword clusters that satisfy both traditional search algorithms and AI-powered search engines.",
  },
  {
    step: "04",
    title: "Citation & Authority Building",
    body: "Consistent NAP (Name, Address, Phone) citations across authoritative directories are a foundational local ranking signal. We audit existing citations for inconsistencies, clean up duplicate listings, and build new citations on high-authority platforms. We also pursue local backlinks from relevant industry sources, local news outlets, and community organizations.",
  },
  {
    step: "05",
    title: "AI Overview & AEO Optimization",
    body: "We optimize your content architecture for AI-generated search results using structured data markup, entity optimization, and answer-first content formatting. This includes implementing FAQPage, LocalBusiness, Service, and HowTo schema — the structured signals that AI search engines use to identify authoritative answers worth surfacing in AI Overviews.",
  },
  {
    step: "06",
    title: "Reporting & Growth Tracking",
    body: "Every client receives monthly performance reports showing keyword rankings, Google Maps position tracking, website traffic, call volume, form submissions, and campaign progress. We believe in complete transparency — you always know exactly what we are doing, why we are doing it, and what results it is producing.",
  },
];

// ─── Case Studies ─────────────────────────────────────────────────────────────
const caseStudies = [
  {
    company: "Precision Electric Co.",
    industry: "Electricians",
    city: "Tampa, FL",
    metric: "+312%",
    metricLabel: "Organic Leads",
    timeframe: "6 Months",
    description: "Took this electrical contractor from page 3 to the #1 spot in Google Maps and tripled their monthly lead volume through GBP optimization, citation building, and service-area content.",
  },
  {
    company: "Cool Air HVAC",
    industry: "HVAC",
    city: "Orlando, FL",
    metric: "#1",
    metricLabel: "Google Maps Rank",
    timeframe: "90 Days",
    description: "Achieved Top 3 Google Maps visibility for 'HVAC repair Orlando' and 14 surrounding service-area keywords within 3 months of campaign launch.",
  },
  {
    company: "Bright Smiles Dental",
    industry: "Healthcare",
    city: "Miami, FL",
    metric: "+65%",
    metricLabel: "New Patients",
    timeframe: "6 Months",
    description: "Increased new patient appointments by 65% through targeted local SEO, Google Business Profile optimization, and a semantic content strategy targeting high-intent dental search queries.",
  },
];

// ─── Why Choose Us ────────────────────────────────────────────────────────────
const whyUs = [
  {
    icon: Award,
    title: "21+ Years of Real SEO Experience",
    desc: "Founded in 2005, Local Ranking Coach has navigated every major Google algorithm update — Panda, Penguin, Pigeon, Possum, BERT, Helpful Content, and the AI Overview rollout. We know what works because we have been doing it longer than most agencies have existed.",
  },
  {
    icon: MapPin,
    title: "Local SEO Is All We Do",
    desc: "We do not dabble in social media management, web design, or brand strategy. Our entire team, every process, and every tool is built around one objective: getting local businesses into the Top 3 of Google Maps and local search results.",
  },
  {
    icon: Users,
    title: "Industry-Specific Strategies",
    desc: "A roofing company and a dental practice have completely different search behaviors, competitive landscapes, and conversion requirements. We build custom strategies for each industry rather than applying generic templates that produce mediocre results.",
  },
  {
    icon: BarChart3,
    title: "Transparent Reporting",
    desc: "Monthly performance reports with clear metrics tied to business outcomes — rankings, traffic, calls, form submissions, and revenue impact. You always know exactly what your investment is producing.",
  },
  {
    icon: Shield,
    title: "White-Hat, Sustainable SEO",
    desc: "We build rankings that last through algorithm updates. No shortcuts, no black-hat link schemes, no keyword stuffing. Our strategies are designed to compound in value over time, not collapse when Google updates its algorithm.",
  },
  {
    icon: Zap,
    title: "Measurable Results in 60–90 Days",
    desc: "Most clients see measurable ranking improvements within 60 to 90 days. We move fast without cutting corners, prioritizing the highest-impact optimizations first to generate early wins while building long-term authority.",
  },
];

// ─── State Hubs ───────────────────────────────────────────────────────────────
const stateHubs = [
  { state: "Florida", slug: "florida", cities: 115 },
  { state: "Texas", slug: "texas", cities: 47 },
  { state: "California", slug: "california", cities: 37 },
  { state: "Georgia", slug: "georgia", cities: 28 },
  { state: "North Carolina", slug: "north-carolina", cities: 24 },
  { state: "Ohio", slug: "ohio", cities: 22 },
  { state: "Illinois", slug: "illinois", cities: 21 },
  { state: "Pennsylvania", slug: "pennsylvania", cities: 20 },
  { state: "Tennessee", slug: "tennessee", cities: 19 },
  { state: "Arizona", slug: "arizona", cities: 18 },
  { state: "Colorado", slug: "colorado", cities: 17 },
  { state: "Washington", slug: "washington", cities: 24 },
];

export default function Home() {
  return (
    <Layout
      title="Local SEO for Businesses | Rank #1 in Google Maps & AI Overviews | Local Ranking Coach"
      description="Local Ranking Coach: Veteran-owned local SEO company founded 2005. We help contractors, doctors & attorneys rank in Top 3 Google Maps, AI Overviews & local search nationwide."
      canonical="https://locranking-7q2qajau.manus.space/"
      schema={schema}
      hreflangLinks={hreflangLinks}
    >
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="gradient-hero pt-32 pb-20 md:pt-40 md:pb-28 relative overflow-hidden" aria-labelledby="hero-heading">
        <div className="absolute inset-0 opacity-5" aria-hidden="true">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full border border-white" />
          <div className="absolute top-40 right-40 w-64 h-64 rounded-full border border-white" />
          <div className="absolute bottom-20 left-20 w-80 h-80 rounded-full border border-white" />
        </div>
        <div className="container relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 border border-gold/40 rounded-full mb-6">
                <Star className="w-4 h-4 text-gold fill-gold" aria-hidden="true" />
                <span className="text-gold text-sm font-semibold">Veteran-Owned · Founded 2005 · 500+ Businesses Served</span>
              </div>
              <h1
                id="hero-heading"
                className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5"
              >
                Local SEO for Businesses That Want to Rank in the{" "}
                <span className="text-gradient-gold">Top 3 of Google Maps & AI Overviews</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed mb-6 max-w-xl">
                Local SEO for business is no longer about getting more clicks that never turn into revenue. It is about showing up in Google Maps, local search results, and AI Overviews when someone is ready to call, book, or request a quote.
              </p>
              <p className="text-white/70 text-base leading-relaxed mb-8 max-w-xl">
                Local Ranking Coach, Inc. is a veteran-owned local SEO company founded in 2005, helping small business owners grow through local SEO, AI SEO, and practical search engine optimization strategies that produce measurable results.
              </p>
              <div className="flex flex-wrap gap-4 mb-10">
                <Link
                  href="/contact"
                  className="inline-flex items-center gap-2 px-7 py-4 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-all duration-200 active:scale-[0.97] shadow-gold text-base"
                >
                  Get Free SEO Audit <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </Link>
                <a
                  href={`tel:${SITE_PHONE}`}
                  className="inline-flex items-center gap-2 px-7 py-4 bg-white/10 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/20 transition-all text-base"
                >
                  <Phone className="w-5 h-5" aria-hidden="true" />
                  {SITE_PHONE}
                </a>
              </div>
              <div className="flex flex-wrap gap-5">
                {["No Long-Term Contracts", "Results in 60–90 Days", "Nationwide Service", "White-Hat SEO Only"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-white/70 text-sm">
                    <CheckCircle className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="animate-fade-in-up animate-delay-200">
              <LeadForm
                variant="dark"
                title="Get Your Free SEO Audit"
                subtitle="Discover what's holding your rankings back — no obligation."
                headingLevel={2}
              />
            </div>
          </div>

          {/* Stats bar */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 pt-10 border-t border-white/10">
            {stats.map((stat, i) => (
              <div key={i} className="text-center animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="font-display text-3xl md:text-4xl font-bold text-gold mb-1">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Local SEO Matters in 2026 ────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="why-local-seo-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Why It Matters</span>
            </div>
            <h2 id="why-local-seo-heading" className="heading-lg text-navy mb-4">
              Why Local SEO Matters in 2026
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              Traditional SEO often focuses on broad organic search results, national keywords, and general website traffic. Local SEO focuses on Google Maps ranking, Local Pack rankings, "near me" searches, and city-specific visibility — the searches that produce phone calls, booked appointments, and revenue.
            </p>
          </div>

          {/* Impact stats grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14">
            {seoImpactStats.map((stat, i) => (
              <div key={i} className="bg-navy rounded-2xl p-6 text-center">
                <div className="font-display text-4xl font-bold text-gold mb-2">{stat.value}</div>
                <p className="text-white/80 text-sm leading-snug">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Three key shifts */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <Brain className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-lg mb-3">AI Overviews Are Changing Search</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                AI-powered search results and Google AI Overviews now appear for many local searches and can reduce clicks to traditional search results. Businesses that are not optimized for AI-generated summaries are losing visibility to competitors who are. Our <Link href="/services/aeo-agency" className="text-gold font-semibold hover:underline">AEO optimization services</Link> ensure your business appears in AI Overviews across Google, ChatGPT, Perplexity, and Bing Copilot.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <Cpu className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-lg mb-3">Mobile & Voice Search Continue to Grow</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                In 2026, more than half of local-intent searches come from mobile devices and voice assistants. These searches are highly specific, highly local, and highly transactional — "HVAC repair near me," "emergency plumber open now," "dentist accepting new patients." Ranking for these queries requires a mobile-first website, optimized Google Business Profile, and voice-search-ready content architecture.
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-lg mb-3">Top 3 Google Maps Rankings Matter</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Businesses that rank in the Top 3 Google Maps positions often receive the majority of calls, clicks, and leads for their target keywords. The businesses in positions 4 through 10 receive a fraction of that traffic. Our <Link href="/services/google-maps-optimization" className="text-gold font-semibold hover:underline">Google Maps optimization</Link> strategy is specifically designed to move your business into those top positions and keep it there.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── What SEO Does for Your Website ───────────────────────────────── */}
      <section className="section-padding bg-gray-50" aria-labelledby="seo-impact-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">SEO Impact</span>
            </div>
            <h2 id="seo-impact-heading" className="heading-lg text-navy mb-4">
              What Professional SEO Services Do for Your Website
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              Search engine optimization is not a single tactic — it is a comprehensive system that improves every dimension of your online visibility, from technical website performance to Google Maps rankings to AI-generated search results. Here is exactly what a properly executed local SEO campaign does for your business.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {seoWebsiteImpacts.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-300">
                <div className="w-11 h-11 bg-navy rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-5 h-5 text-gold" aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-navy text-base mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Services Overview ─────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="services-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Our Services</span>
            </div>
            <h2 id="services-heading" className="heading-lg text-navy mb-4">
              Our Local SEO Services for Small Business
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              From <Link href="/services/google-business-profile" className="text-gold font-semibold hover:underline">Google Business Profile optimization</Link> to full-scale local SEO campaigns, we offer every service your business needs to dominate local search. Every service is built around one goal: more calls, more leads, and more revenue from organic search.
            </p>
          </div>

          {/* GBP + AI Overview callout */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-navy rounded-2xl p-8 text-white">
              <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-xl mb-3">Google Business Profile SEO</h3>
              <p className="text-white/80 text-sm leading-relaxed mb-4">
                Google Business Profile optimization is the foundation of Google Maps SEO and local visibility. A properly optimized GBP is the single highest-impact action most local businesses can take to improve their search rankings.
              </p>
              <p className="text-white/70 text-sm leading-relaxed mb-5">
                We optimize your primary and secondary categories, services and descriptions, photos and posts, NAP consistency, review generation strategy, and local citations — the complete set of signals Google uses to rank businesses in the Local Pack.
              </p>
              <Link href="/services/google-business-profile" className="inline-flex items-center gap-2 text-gold font-semibold text-sm hover:underline">
                Learn About GBP Optimization <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <Layers className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-xl mb-3">AI Overview SEO & AEO Optimization</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                AI Overview SEO helps your business appear in AI-generated search summaries across Google and Bing. As AI Overviews increasingly appear above traditional organic results, businesses that are not optimized for these summaries are losing significant visibility.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-5">
                We implement the structured data signals AI search engines require: LocalBusiness schema, Service schema, FAQPage schema, and entity-optimized content architecture that positions your business as the authoritative answer for your target queries.
              </p>
              <Link href="/services/aeo-agency" className="inline-flex items-center gap-2 text-gold font-semibold text-sm hover:underline">
                Explore AEO Services <ChevronRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          {/* Services grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {services.map((service) => (
              <Link
                key={service.slug}
                href={`/services/${service.slug}`}
                className="group bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                <div className="w-10 h-10 rounded-lg bg-navy/5 flex items-center justify-center mb-3 group-hover:bg-gold/10 transition-colors">
                  <TrendingUp className="w-5 h-5 text-navy group-hover:text-gold transition-colors" aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-navy text-sm mb-1.5 group-hover:text-gold transition-colors">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">{service.shortDesc}</p>
                <div className="flex items-center gap-1 mt-3 text-gold-accessible text-xs font-semibold">
                  Learn More <ArrowRight className="w-3 h-3" aria-hidden="true" />
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              href="/services"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200"
            >
              View All Services <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── AEO Services ────────────────────────────────────────────────────── */}
      <section className="section-padding bg-gray-50" aria-labelledby="aeo-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">AI Search Optimization</span>
            </div>
            <h2 id="aeo-heading" className="heading-lg text-navy mb-4">
              Dominate AI-Powered Search Results
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              ChatGPT, Gemini, Perplexity, and Google AI Overviews are now where your customers find their next service provider. Our AEO Agency suite ensures your brand is the answer they get.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-10">
            {([
              {
                title: "ChatGPT SEO",
                desc: "Get cited and recommended inside ChatGPT Search, Claude, Gemini, Perplexity, and every major AI assistant platform.",
                href: "/services/aeo-agency/chatgpt-seo",
                gradient: "from-navy to-[#1a2f5a]",
                btnClass: "bg-navy hover:bg-[#1a2f5a]",
                icon: <MessageSquare className="w-6 h-6 text-gold" aria-hidden="true" />,
                points: ["ChatGPT Search citation optimization","OpenAI trust signal building","Conversational query targeting","AI-first content architecture"],
              },
              {
                title: "Claude SEO",
                desc: "Optimize your brand for Anthropic's Claude — the AI platform that prioritizes E-E-A-T signals and deep subject-matter expertise.",
                href: "/services/aeo-agency/claude-seo",
                gradient: "from-violet-700 to-purple-800",
                btnClass: "bg-violet-700 hover:bg-purple-800",
                icon: <Brain className="w-6 h-6 text-violet-200" aria-hidden="true" />,
                points: ["Anthropic Claude citations","E-E-A-T authority building","Expert content architecture","Professional services focus"],
              },
              {
                title: "Gemini SEO",
                desc: "Get cited in Google Gemini, AI Overviews, and AI Mode through semantic SEO, entity optimization, and E-E-A-T strategy.",
                href: "/services/aeo-agency/gemini-seo",
                gradient: "from-blue-600 to-indigo-700",
                btnClass: "bg-blue-600 hover:bg-indigo-700",
                icon: <Globe className="w-6 h-6 text-blue-200" aria-hidden="true" />,
                points: ["Google AI Overviews optimization","AI Mode (Search Labs) visibility","Knowledge Graph entity SEO","Semantic content architecture"],
              },
              {
                title: "Perplexity SEO",
                desc: "Get cited in Perplexity AI — the fastest-growing AI search engine — through RAG optimization, entity SEO, and answer-first content.",
                href: "/services/aeo-agency/perplexity-seo",
                gradient: "from-[#1a1a2e] to-[#16213e]",
                btnClass: "bg-[#1a1a2e] hover:bg-[#16213e]",
                icon: <Zap className="w-6 h-6 text-[#00b4d8]" aria-hidden="true" />,
                points: ["Perplexity AI citations","PerplexityBot crawl access","RAG content architecture","Answer-first formatting"],
              },
              {
                title: "LLM SEO",
                desc: "Optimize your brand across all major large language models — ChatGPT, Gemini, Claude, Perplexity, and Copilot — through a unified AI citation strategy.",
                href: "/services/aeo-agency/llm-seo",
                gradient: "from-slate-700 to-slate-900",
                btnClass: "bg-slate-700 hover:bg-slate-900",
                icon: <Network className="w-6 h-6 text-sky-300" aria-hidden="true" />,
                points: ["Multi-platform AI citation strategy","Entity & semantic SEO","AI crawlability optimization","Cross-LLM visibility tracking"],
              },
              {
                title: "Generative Engine Optimization",
                desc: "Appear in Google AI Overviews, Bing Copilot, and AI-generated summaries across every major generative search platform.",
                href: "/services/aeo-agency/geo-seo",
                gradient: "from-[#7a5d0c] to-[#5c4508]",
                btnClass: "bg-[#7a5d0c] hover:bg-[#5c4508]",
                icon: <Layers className="w-6 h-6 text-yellow-100" aria-hidden="true" />,
                points: ["Google AI Overviews optimization","LLM content architecture","AI citation structure","Semantic entity mapping"],
              },
              {
                title: "AI Search Optimization",
                desc: "Full-spectrum AI search optimization using the MERIT Framework to maximize visibility across every AI-powered search environment.",
                href: "/services/aeo-agency/aio-seo",
                gradient: "from-[#1e4d8c] to-navy",
                btnClass: "bg-[#1e4d8c] hover:bg-navy",
                icon: <Cpu className="w-6 h-6 text-gold" aria-hidden="true" />,
                points: ["MERIT Framework implementation","Voice search optimization","Featured snippet targeting","Cross-platform AI monitoring"],
              },
            ] as { title: string; desc: string; href: string; gradient: string; btnClass: string; icon: React.ReactNode; points: string[] }[]).map(({ title, desc, href, gradient, btnClass, icon, points }) => (
              <div key={title} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden group">
                <div className={`bg-gradient-to-br ${gradient} p-6 text-white`}>
                  <div className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center mb-3 group-hover:bg-white/20 transition-colors">
                    {icon}
                  </div>
                  <h3 className="text-base font-extrabold mb-1.5">{title}</h3>
                  <p className="text-xs text-white/80 leading-relaxed">{desc}</p>
                </div>
                <div className="p-6">
                  <ul className="space-y-1.5 mb-5 text-sm text-gray-600">
                    {points.map((item) => (
                      <li key={item} className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-gold shrink-0" aria-hidden="true" />{item}
                      </li>
                    ))}
                  </ul>
                  <Link
                    href={href}
                    className={`flex items-center justify-between gap-2 ${btnClass} text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-colors group/btn`}
                  >
                    Explore Service
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link
              href="/services/aeo-agency"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200"
            >
              View Full AEO Agency Suite <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Industries Served ─────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="industries-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Industries We Help Rank</span>
            </div>
            <h2 id="industries-heading" className="heading-lg text-navy mb-4">
              SEO Built for Your Industry
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              Generic SEO does not work. We have built industry-specific strategies for the businesses that need local search the most. As the owner of roofing, HVAC, and electrical companies, our founder has first-hand experience ranking in the industries we serve — not just theoretical knowledge.
            </p>
          </div>

          {/* Industry callouts */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-lg mb-3">Construction & Home Service Companies</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We have real-life experience when it comes to home service SEO. As the owner of roofing, HVAC, and electrical companies, our founder has first-hand knowledge of what it takes to rank in these competitive local markets. We help contractors rank for emergency services, local searches, and service-area keywords.
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  { label: "SEO for Electricians", href: "/industries/electricians-seo" },
                  { label: "SEO for Plumbers", href: "/industries/plumbing-seo" },
                  { label: "HVAC SEO Services", href: "/industries/hvac-seo" },
                  { label: "SEO for General Contractors", href: "/industries/general-contractors-seo" },
                  { label: "Roofing SEO", href: "/industries/roofing-seo" },
                ].map((item) => (
                  <li key={item.href} className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" />
                    <Link href={item.href} className="text-navy text-sm font-medium hover:text-gold transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-lg mb-3">Medical SEO Services</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We support doctors, dentists, chiropractors, and specialty clinics with patient-focused SEO strategies that comply with healthcare marketing guidelines. Our medical SEO approach targets high-intent patient searches and optimizes your Google Business Profile for the specific categories that drive appointment bookings.
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  { label: "Healthcare SEO", href: "/industries/healthcare-seo" },
                  { label: "Dental SEO", href: "/industries/dental-seo" },
                  { label: "Medical Practice SEO", href: "/industries/healthcare-seo" },
                ].map((item) => (
                  <li key={item.label} className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" />
                    <Link href={item.href} className="text-navy text-sm font-medium hover:text-gold transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-2xl p-7 border border-gray-100">
              <div className="w-12 h-12 bg-navy rounded-xl flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-gold" aria-hidden="true" />
              </div>
              <h3 className="font-display font-bold text-navy text-lg mb-3">Legal SEO Services</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We help law firms improve visibility for personal injury, family law, criminal defense, and other legal services. Legal SEO requires a specialized approach that balances aggressive local ranking strategies with bar association advertising guidelines and the high-trust E-E-A-T signals that Google requires for YMYL (Your Money or Your Life) content.
              </p>
              <ul className="space-y-2 mb-5">
                {[
                  { label: "Legal SEO Services", href: "/industries/legal-seo" },
                  { label: "Law Firm SEO", href: "/industries/legal-seo" },
                ].map((item) => (
                  <li key={item.label} className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" />
                    <Link href={item.href} className="text-navy text-sm font-medium hover:text-gold transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* All industries grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {industries.map((industry) => (
              <Link
                key={industry.slug}
                href={`/industries/${industry.slug}`}
                className="group bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-300 hover:-translate-y-0.5 text-center"
              >
                <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-3 group-hover:bg-gold/10 transition-colors">
                  <Globe className="w-6 h-6 text-navy group-hover:text-gold transition-colors" aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-navy text-sm group-hover:text-gold transition-colors">
                  {industry.title}
                </h3>
                <p className="text-gray-500 text-xs mt-1 leading-tight">{industry.desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              href="/industries"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-navy text-navy font-semibold rounded-lg hover:bg-navy hover:text-white transition-all duration-200"
            >
              View All Industries <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ─────────────────────────────────────────────────── */}
      <section className="section-padding bg-gray-50" aria-labelledby="why-heading">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-5">
                <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Why Local Ranking Coach</span>
              </div>
              <h2 id="why-heading" className="heading-lg text-navy mb-5">
                Why Businesses Trust Local Ranking Coach
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                Local Ranking Coach, Inc. is a U.S.-based, veteran-owned SEO company focused on local visibility, lead generation, and business growth. Since 2005, we have built SEO campaigns for contractors, doctors, and attorneys in competitive markets across the United States.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                We use white-hat SEO strategies and provide transparent reporting on rankings, traffic, calls, and campaign progress. Our approach is built on sustainable growth rather than risky shortcuts or spam tactics — the kind of SEO that withstands algorithm updates and compounds in value over time.
              </p>

              {/* Trust signals */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-6">
                <h3 className="font-display font-bold text-navy text-base mb-4">Trust Signals That Set Us Apart</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Award, label: "Veteran-Owned Business" },
                    { icon: Star, label: "Verified Google Reviews" },
                    { icon: BarChart3, label: "Before & After Rankings" },
                    { icon: Users, label: "Client Testimonials" },
                    { icon: FileText, label: "SEO Audit Examples" },
                    { icon: Shield, label: "21+ Years Experience" },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-2">
                      <item.icon className="w-4 h-4 text-gold flex-shrink-0" aria-hidden="true" />
                      <span className="text-gray-700 text-sm">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col gap-3 mb-8">
                {[
                  "Specialized in trades, medical, and legal businesses",
                  "Average 3× increase in qualified leads within 6 months",
                  "95% client retention rate",
                  "Transparent monthly reporting with no hidden metrics",
                  "No long-term contracts required",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-gold flex-shrink-0" aria-hidden="true" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                href="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white font-semibold rounded-lg hover:bg-navy-light transition-all duration-200"
              >
                Learn About Us <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyUs.map((item, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl p-5 border border-gray-100 hover:border-gold/30 hover:shadow-sm transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-navy flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-gold" aria-hidden="true" />
                  </div>
                  <h3 className="font-display font-bold text-navy text-sm mb-1.5">{item.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Our Process ───────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="process-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Our Process</span>
            </div>
            <h2 id="process-heading" className="heading-lg text-navy mb-4">
              Our Local SEO Process
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
              A proven six-step system built on 21 years of local SEO experience. Every campaign follows the same rigorous process — customized for your industry, market, and goals.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, i) => (
              <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-100 hover:border-gold/30 hover:shadow-sm transition-all duration-300">
                <div className="font-display text-5xl font-bold text-navy/10 absolute top-5 right-6 select-none" aria-hidden="true">{step.step}</div>
                <div className="w-10 h-10 bg-navy rounded-xl flex items-center justify-center mb-4">
                  <span className="text-gold font-bold text-sm">{step.step}</span>
                </div>
                <h3 className="font-display font-bold text-navy text-base mb-3">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Service Plans ─────────────────────────────────────────────────── */}
      <section className="section-padding bg-navy" aria-labelledby="plans-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/20 border border-gold/30 rounded-full mb-4">
              <span className="text-gold-light text-xs font-semibold uppercase tracking-wider">Transparent Pricing</span>
            </div>
            <h2 id="plans-heading" className="heading-lg text-white mb-4">
              Affordable SEO Service Plans for Small Businesses
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-white/70 max-w-2xl mx-auto text-lg leading-relaxed">
              Affordable SEO should focus on sustainable growth rather than risky shortcuts or spam tactics. Our service plans are designed to deliver measurable results at every budget level — with transparent pricing and no long-term contracts.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {servicePlans.map((plan, i) => (
              <div
                key={i}
                className={`rounded-2xl p-8 border transition-all duration-300 ${
                  plan.highlight
                    ? "bg-gold border-gold shadow-xl scale-[1.02]"
                    : "bg-white/5 border-white/10 hover:border-gold/30"
                }`}
              >
                {plan.highlight && (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-navy text-gold text-xs font-bold rounded-full mb-4">
                    <Star className="w-3 h-3 fill-gold" aria-hidden="true" /> Most Popular
                  </div>
                )}
                <h3 className={`font-display text-2xl font-bold mb-1 ${plan.highlight ? "text-navy" : "text-white"}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm font-semibold mb-2 ${plan.highlight ? "text-navy/70" : "text-gold"}`}>
                  {plan.tagline}
                </p>
                <p className={`text-xl font-bold mb-1 ${plan.highlight ? "text-navy" : "text-gold"}`}>
                  {plan.price}
                </p>
                <p className={`text-xs font-semibold mb-4 ${plan.highlight ? "text-navy/60" : "text-white/50"}`}>
                  {(plan as typeof plan & { setupFee?: string }).setupFee}
                </p>
                <p className={`text-sm leading-relaxed mb-6 ${plan.highlight ? "text-navy/80" : "text-white/70"}`}>
                  {plan.description}
                </p>
                <ul className="space-y-2.5 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.highlight ? "text-navy" : "text-gold"}`} aria-hidden="true" />
                      <span className={`text-sm ${plan.highlight ? "text-navy/90" : "text-white/80"}`}>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  href={plan.href}
                  className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm transition-all duration-200 ${
                    plan.highlight
                      ? "bg-navy text-white hover:bg-navy-light"
                      : "bg-gold text-navy hover:bg-gold-light"
                  }`}
                >
                  {plan.cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-white/50 text-sm mt-8">
            All plans include a free initial SEO audit. No setup fees. No long-term contracts. Cancel anytime.
          </p>
        </div>
      </section>

      {/* ── Case Studies ──────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="case-studies-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Proven Results</span>
            </div>
            <h2 id="case-studies-heading" className="heading-lg text-navy mb-4">
              Real Results for Real Businesses
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              Don't take our word for it. See the measurable results we've delivered for businesses just like yours.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {caseStudies.map((cs, i) => (
              <div
                key={i}
                className="bg-gray-50 border border-gray-100 rounded-2xl p-6 hover:border-gold/30 hover:shadow-md transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-gold text-xs font-semibold uppercase tracking-wider mb-1">{cs.industry}</p>
                    <h3 className="font-display font-bold text-navy text-base">{cs.company}</h3>
                    <p className="text-gray-500 text-xs">{cs.city}</p>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-bold text-gold">{cs.metric}</div>
                    <div className="text-gray-500 text-xs">{cs.metricLabel}</div>
                  </div>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{cs.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-gold-accessible text-xs font-semibold bg-gold/10 px-2 py-1 rounded-full">
                    {cs.timeframe}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              href="/case-studies"
              className="inline-flex items-center gap-2 px-6 py-3 bg-navy text-white font-semibold rounded-lg hover:bg-navy-light transition-all duration-200"
            >
              View All Case Studies <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── State Hubs / Nationwide Coverage ─────────────────────────────── */}
      <section className="section-padding bg-gray-50" aria-labelledby="locations-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Nationwide Coverage</span>
            </div>
            <h2 id="locations-heading" className="heading-lg text-navy mb-4">
              Local SEO Services Across the United States
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-6" aria-hidden="true" />
            <p className="text-gray-600 max-w-3xl mx-auto text-lg leading-relaxed">
              We provide local SEO services for businesses in every major U.S. market. Our state-specific SEO hubs give you access to city-level strategies, local market data, and location-specific content that drives rankings in your exact service area.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-10">
            {stateHubs.map((hub) => (
              <Link
                key={hub.slug}
                href={`/locations/${hub.slug}`}
                className="group bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-gold/30 transition-all duration-300 hover:-translate-y-0.5 text-center"
              >
                <div className="w-10 h-10 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-2 group-hover:bg-gold/10 transition-colors">
                  <MapPin className="w-5 h-5 text-navy group-hover:text-gold transition-colors" aria-hidden="true" />
                </div>
                <h3 className="font-display font-bold text-navy text-xs group-hover:text-gold transition-colors mb-1">
                  {hub.state}
                </h3>
                <p className="text-gray-400 text-xs">{hub.cities} cities</p>
              </Link>
            ))}
          </div>
          <div className="bg-navy rounded-2xl p-8 text-center">
            <h3 className="font-display font-bold text-white text-xl mb-3">
              Don't See Your State? We Cover All 50 States.
            </h3>
            <p className="text-white/70 text-sm mb-6 max-w-xl mx-auto">
              Our nationwide local SEO service covers every U.S. market. Browse our complete location directory to find city-specific SEO strategies, local market data, and service-area content for your exact location.
            </p>
            <Link
              href="/locations"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-navy font-bold rounded-lg hover:bg-gold-light transition-all duration-200"
            >
              Browse All Locations <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────────── */}
      <TestimonialsSection testimonials={testimonials} />

      {/* ── FAQs ─────────────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" aria-labelledby="faq-heading">
        <div className="container">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gold/10 border border-gold/20 rounded-full mb-4">
              <span className="text-gold-accessible text-xs font-semibold uppercase tracking-wider">Frequently Asked Questions</span>
            </div>
            <h2 id="faq-heading" className="heading-lg text-navy mb-4">
              Common Questions About Local SEO
            </h2>
            <div className="w-16 h-1 bg-gold mx-auto rounded-full mb-4" aria-hidden="true" />
            <p className="text-gray-600 max-w-2xl mx-auto">
              Answers to the most common questions from business owners about local SEO, Google Maps rankings, AI Overview optimization, and what to expect from a professional SEO campaign.
            </p>
          </div>
          <FAQSection faqs={faqs} />
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <CTASection />

      {/* ── Contact Form ─────────────────────────────────────────────────── */}
      <section className="section-padding bg-white" id="contact" aria-labelledby="contact-heading">
        <div className="container">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <h2 id="contact-heading" className="heading-lg text-navy mb-3">
                Get Started with Local Ranking Coach, Inc.
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                If you want more calls, leads, patients, or consultations from Google Maps and AI Overviews, contact Local Ranking Coach, Inc. today. We provide free SEO consultations, Google Maps audits, and AI Overview analysis for local businesses — no obligation, no spam.
              </p>
            </div>
            <LeadForm
              title="Request Your Free SEO Audit"
              subtitle="No obligation. No spam. Just actionable insights for your business."
            />
          </div>
        </div>
      </section>

      {/* ── Internal Links Footer ─────────────────────────────────────────── */}
      <section className="py-10 bg-gray-50 border-t border-gray-100" aria-labelledby="explore-heading">
        <div className="container">
          <h2 id="explore-heading" className="text-base font-bold text-navy mb-6 text-center">Explore Our SEO Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <h3 className="text-sm font-bold text-navy mb-3 uppercase tracking-wide">Services</h3>
              <ul className="space-y-2">
                {[
                  { label: "Local SEO Services", href: "/services/local-seo" },
                  { label: "Google Business Profile", href: "/services/google-business-profile" },
                  { label: "Google Maps Optimization", href: "/services/google-maps-optimization" },
                  { label: "Technical SEO", href: "/services/technical-seo" },
                  { label: "Content Marketing", href: "/services/content-marketing" },
                  { label: "Link Building", href: "/services/link-building" },
                  { label: "AEO Agency", href: "/services/aeo-agency" },
                  { label: "SEO Pricing", href: "/pricing" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link href={item.href} className="text-gray-600 text-sm hover:text-gold transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy mb-3 uppercase tracking-wide">Industries</h3>
              <ul className="space-y-2">
                {[
                  { label: "Roofing SEO", href: "/industries/roofing-seo" },
                  { label: "HVAC SEO", href: "/industries/hvac-seo" },
                  { label: "Plumbing SEO", href: "/industries/plumbing-seo" },
                  { label: "Electricians SEO", href: "/industries/electricians-seo" },
                  { label: "Healthcare SEO", href: "/industries/healthcare-seo" },
                  { label: "Legal SEO", href: "/industries/legal-seo" },
                  { label: "Real Estate SEO", href: "/industries/real-estate-seo" },
                  { label: "Automotive SEO", href: "/industries/automotive-seo" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link href={item.href} className="text-gray-600 text-sm hover:text-gold transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy mb-3 uppercase tracking-wide">Top Markets</h3>
              <ul className="space-y-2">
                {[
                  { label: "Florida SEO", href: "/locations/florida" },
                  { label: "Texas SEO", href: "/locations/texas" },
                  { label: "California SEO", href: "/locations/california" },
                  { label: "Georgia SEO", href: "/locations/georgia" },
                  { label: "North Carolina SEO", href: "/locations/north-carolina" },
                  { label: "Ohio SEO", href: "/locations/ohio" },
                  { label: "All Locations", href: "/locations" },
                  { label: "SEO Blog", href: "/blog" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link href={item.href} className="text-gray-600 text-sm hover:text-gold transition-colors">{item.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
